import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ToastrService} from 'ngx-toastr';

import {IPETab} from 'src/app/interfaces/accountant.interfaces';
import {IDropdownObject} from 'src/app/interfaces/dropdown-object.interface';
import {AccessManagementService} from 'src/app/services/access-management.service';
import {CloseHandlerService} from 'src/app/services/close-handler.service';
import {EmployeeService} from 'src/app/services/employee.service';
import {roles} from 'src/app/shared/utils/roles';
import {Utils} from 'src/app/shared/utils/utils';

@UntilDestroy()
@Component({
  selector: 'app-pe-data',
  templateUrl: './pe-data.component.html',
  styleUrls: ['./pe-data.component.scss'],
})
export class PeDataComponent implements OnInit {
  public isEditable = false;
  public form!: UntypedFormGroup;
  public employeeId!: number;
  public whoMakesTheReportDropdown: IDropdownObject[] = [];
  public whoPaysTaxesDropdown: IDropdownObject[] = [];
  public agreementDropdown: IDropdownObject[] = [];
  public requiredErrorMessage = 'The field is required';
  public isPECreated!: number;
  public firstLoad = true;
  public formValueDefault!: IPETab;
  public changed = false;
  public isEqualObjects = Utils.isEqualObjects;
  public isCEO: boolean = false;
  public isAccountant: boolean = false;
  public ableToEdit: boolean = false;

  @ViewChild('saveButton') saveButton!: ElementRef;

  constructor(
    private _fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private employee: EmployeeService,
    private notifier: ToastrService,
    private closeHandler: CloseHandlerService,
    private access: AccessManagementService
  ) {}

  ngOnInit(): void {
    this.employeeId = this.route.parent!.snapshot.params['id'];
    this.form = this._fb.group({
      id: new UntypedFormControl(null),
      employeeId: new UntypedFormControl(null),
      taxNumber: new UntypedFormControl(null),
      descriptionNumber: new UntypedFormControl(null),
      peRegistrationDate: new UntypedFormControl(null),
      addressRegistration: new UntypedFormControl(null),
      addressRegistrationUA: new UntypedFormControl(null),
      registrationDateByTaxpayer: new UntypedFormControl(null),
      keyName: new UntypedFormControl(null),
      key: new UntypedFormControl(null),
      keysDateEnd: new UntypedFormControl(null),
      keyPassword: new UntypedFormControl(null),
      whoMakesReports: new UntypedFormControl(null),
      whoMakesReportsComment: new UntypedFormControl(null),
      whoPaysTaxes: new UntypedFormControl(null),
      whoPaysTaxesComment: new UntypedFormControl(null),
      stTaxPaymentDate: new UntypedFormControl(null),
      stTaxPaymentDateComment: new UntypedFormControl(null),
      sstTaxPaymentDate: new UntypedFormControl(null),
      sstTaxPaymentDateComment: new UntypedFormControl(null),
      stTaxPaymentLastDate: new UntypedFormControl(null),
      stTaxPaymentLastDateComment: new UntypedFormControl(null),
      sstTaxPaymentLastDate: new UntypedFormControl(null),
      sstTaxPaymentLastDateComment: new UntypedFormControl(null),
      report: new UntypedFormControl(null),
      reportComment: new UntypedFormControl(null),
    });

    this.employee
      .getPEData(this.employeeId)
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        this.form.setValue(response);

        if (response.whoMakesReports) {
          this.whoMakesTheReportDropdown = [response.whoMakesReports];
        }
        if (response.whoPaysTaxes) {
          this.whoPaysTaxesDropdown = [response.whoPaysTaxes];
        }
        if (response.report) {
          this.agreementDropdown = [response.report];
        }
        setTimeout(() => (this.formValueDefault = this.form.value));
      });

    this.employee
      .isEmployeeHasPE(this.employeeId)
      .pipe(untilDestroyed(this))
      .subscribe(response => (this.isPECreated = response));

    this.closeHandler.notSaveForm.pipe(untilDestroyed(this)).subscribe(() => {
      this.isEditable = false;
      this.form.markAsUntouched();
      this.changed = true;
      this.form.patchValue(this.formValueDefault);
      this.changed = false;
    });

    this.form.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((value: IPETab) => {
        if (
          value &&
          this.isEditable &&
          !this.changed &&
          !this.isEqualObjects(value, this.formValueDefault)
        ) {
          this.closeHandler.notSaved = true;
        }
      });
    this.closeHandler.clickSavePopup
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.saveButton.nativeElement.click();
      });
    this.isAccountant = this.access.checkPermissions(
      roles.ACCOUNTANT_DEPARTMENT
    );
    this.isCEO = this.access.checkPermissions(roles.CEO);
    if (this.isAccountant || this.isCEO) this.ableToEdit = true;
  }
  /**
   * Function that is binded on Save button click. If the PE was created call PUT method, otherwise call POST method from the service
   */
  saveChanges() {
    if (this.isPECreated) {
      this.formValueDefault = this.form.value;
      this.closeHandler.notSaved = false;
      this.employee
        .updatePE(this.form.value)
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.isEditable = !this.isEditable;
          this.notifier.success('Saved');
        });
      return;
    }
    this.formValueDefault = this.form.value;
    this.closeHandler.notSaved = false;
    this.employee
      .addNewPE(this.form.value)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.isEditable = !this.isEditable;
        this.notifier.success('Saved');
      });
  }
  /**
   * Function that switches between edit mode
   * If we call it for the first time, GET dropdowns' data from backend
   */
  switchEdit() {
    if (this.firstLoad) {
      this.employee
        .getBankInfoDropdowns(this.employeeId)
        .pipe(untilDestroyed(this))
        .subscribe(response => {
          this.whoMakesTheReportDropdown = response.DealsWithTaxes;
          this.whoPaysTaxesDropdown = response.DealsWithTaxes;
          this.agreementDropdown = response.YesNoEnum;
        });
      this.firstLoad = false;
    }
    this.isEditable = !this.isEditable;
    this.form.markAsUntouched();
  }
}
